import { Fragment } from 'react';
import { sprintf } from 'sprintf-js';
import { styled } from 'styled-components';

import type { ApiListingMiniExtra } from 'api/types/ApiListingMini';
import { ExpandButton } from 'components/ExpandButton/ExpandButton';
import { StyledVersionedRouterLink } from 'components/Link/StyledVersionedRouterLink';
import { Table } from 'components/Table/Table';
import { ToggleHelperContainer } from 'containers/ToggleHelperContainer';
import { useIsClient } from 'hooks/useIsClient';
import { useViewportBreakpointValue } from 'hooks/viewport/useViewportBreakpointValue';
import { colors, spacing } from 'theme/theme';
import { getWorkLocationString } from 'utils/address/getWorkLocationString';
import { trackClicked } from 'utils/analytics/track/trackClicked';
import { distanceInWordsToNow } from 'utils/date';

const MAX_LISTINGS = 10;

const Styledtable = styled(Table)<{ $stacked?: boolean }>`
  ${({ $stacked }) =>
    $stacked &&
    `tbody > tr:nth-of-type(odd) {
    background-color: ${colors.backgroundGrey};
  }
  tr > td {
    display: table-row;
  }`}
`;

function Listings({
  listingType,
  listings,
}: {
  listingType: 'JOB' | 'INTERNSHIP' | 'VOLOP' | 'EVENT';
  listings: ApiListingMiniExtra[];
}) {
  const isStacked = useViewportBreakpointValue({ xs: true, sm: false });
  const isClient = useIsClient();

  return (
    <Styledtable
      $stacked={isStacked}
      headings={[]}
      data={listings.map((listing) => [
        <StyledVersionedRouterLink
          key={listing.id}
          to={listing.url[CURRENT_LOCALE()]}
          onClick={() =>
            trackClicked('Current Listings on Org Page', {
              listing_type: listingType,
            })
          }
        >
          {listing.name}
        </StyledVersionedRouterLink>,

        <Fragment key="location">
          {!listing.address
            ? getText('Online/Virtual')
            : getWorkLocationString({
                remoteOptions: {
                  remoteZone: listing.remoteZone,
                  remoteCountry: listing.remoteCountry,
                  remoteState: listing.remoteState,
                },
                address: listing.address,
              })}
          {listing.locationType === 'REMOTE' && (
            <span style={{ marginLeft: spacing[6] }}>
              {sprintf('(%(text)s)', { text: getText('Remote') })}
            </span>
          )}
        </Fragment>,

        isClient
          ? sprintf(getText('Posted %(postedAt)s'), {
              postedAt: distanceInWordsToNow(listing.firstPublished),
            })
          : '...',
      ])}
    />
  );
}

type Props = {
  listingType: 'JOB' | 'INTERNSHIP' | 'VOLOP' | 'EVENT';
  listings: ApiListingMiniExtra[];
};

export function ViewOrgPageTruncatedListings({ listingType, listings }: Props) {
  const hasMore = listings.length > MAX_LISTINGS;

  if (hasMore) {
    return (
      <ToggleHelperContainer>
        {(showMore, toggleShowMore) => (
          <>
            <div style={{ marginBottom: 10 }}>
              <Listings
                listingType={listingType}
                listings={showMore ? listings : listings.slice(0, MAX_LISTINGS)}
              />
            </div>

            <ExpandButton onClick={() => toggleShowMore()} open={showMore}>
              {showMore
                ? getText('View less')
                : sprintf(getText('View %(count)s more'), {
                    count: listings.length - MAX_LISTINGS,
                  })}
            </ExpandButton>
          </>
        )}
      </ToggleHelperContainer>
    );
  }

  return <Listings listingType={listingType} listings={listings} />;
}
